import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Axios from 'axios';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useScrollLock from '../hooks/useScrollLock';
import { convertIfHeic } from '../utils/convertHeic';
import { displayName } from '../utils/i18nDisplay';
import Swal from '../utils/swal';

import {
  createCategory,
  deleteCategory,
  listCategories,
  reorderCategories,
  updateCategory,
} from '../actions/categoryActions';
import {
  createGalleryImage,
  deleteGalleryImage,
  listGalleryImages,
  reorderGalleryImages,
  updateGalleryImage,
} from '../actions/galleryActions';
import {
  CATEGORY_CREATE_RESET,
  CATEGORY_DELETE_RESET,
  CATEGORY_UPDATE_RESET,
} from '../constants/categoryConstants';
import {
  GALLERY_IMAGE_CREATE_RESET,
  GALLERY_IMAGE_DELETE_RESET,
  GALLERY_IMAGE_UPDATE_RESET,
} from '../constants/galleryConstants';
import LoadingOverlay from './LoadingOverlay';
import MessageBox from './MessageBox';
import Placeholder from './Placeholder';

// ─── Column layout helpers ────────────────────────────────────────────────────

function getColCount() {
  const w = window.innerWidth;
  if (w >= 992) return 4;
  if (w >= 768) return 3;
  if (w >= 576) return 2;
  return 1;
}

function useColCount() {
  const [colCount, setColCount] = useState(getColCount);
  useEffect(() => {
    const onResize = () => setColCount(getColCount());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return colCount;
}

// ─── Sortable category chip ───────────────────────────────────────────────────

function SortableCategoryChip({ id, name, dbCat, isActive, onEdit, onDelete }) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isActive ? 0 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 16,
        padding: '2px 4px 2px 6px',
        background: '#e0e0e0',
        fontSize: '0.8rem',
        fontFamily: 'inherit',
        whiteSpace: 'nowrap',
        cursor: dbCat ? 'grab' : 'default',
      }}
      {...(dbCat ? { ...attributes, ...listeners } : {})}
    >
      <span style={{ marginRight: 2, userSelect: 'none' }}>{name}</span>
      {dbCat && (
        <>
          <Tooltip title={t('admin.rename')}>
            <IconButton
              size="small"
              sx={{ padding: '2px' }}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
            >
              <EditIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('admin.deleteProduct')}>
            <IconButton
              size="small"
              sx={{ padding: '2px' }}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            >
              <DeleteIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        </>
      )}
    </div>
  );
}

// ─── Shared IntersectionObserver for all lazy images ─────────────────────────

const _lazyCallbacks = new Map();
const _lazyObserver =
  typeof IntersectionObserver !== 'undefined'
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const cb = _lazyCallbacks.get(entry.target);
              if (cb) {
                cb();
                _lazyObserver.unobserve(entry.target);
                _lazyCallbacks.delete(entry.target);
              }
            }
          });
        },
        { rootMargin: '300px' }
      )
    : null;

// Persists across remounts so images don't blink when columns recalculate
const loadedImageCache = new Set();

function LazyImg({ src, alt, initialVisible = false, onLoad, ...props }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(initialVisible);

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el || !_lazyObserver) return;
    _lazyCallbacks.set(el, () => setVisible(true));
    _lazyObserver.observe(el);
    return () => {
      _lazyObserver.unobserve(el);
      _lazyCallbacks.delete(el);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!visible || !onLoad) return;
    const img = ref.current;
    if (!img) return;
    if (img.complete && img.naturalWidth > 0) {
      onLoad();
      return;
    }
    img.addEventListener('load', onLoad);
    return () => img.removeEventListener('load', onLoad);
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  return <img ref={ref} src={visible ? src : undefined} alt={alt} {...props} />;
}

// ─── Plain card (used in DragOverlay) ────────────────────────────────────────

function PlainCard({ item }) {
  return (
    <div className="gallery-image gallery-admin-card-wrapper">
      <img
        src={item.image}
        alt={item.description || item.category}
        style={{ display: 'block', width: '100%' }}
      />
    </div>
  );
}

// ─── Sortable card ────────────────────────────────────────────────────────────

const SortableCard = memo(function SortableCard({ item, onEdit, onDelete, isActive }) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: item._id,
  });
  const [imgLoaded, setImgLoaded] = useState(() => loadedImageCache.has(item._id));

  const aspectRatio =
    !imgLoaded && item.width && item.height ? `${item.width}/${item.height}` : undefined;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isActive ? 0 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="gallery-image gallery-admin-card-wrapper">
      <Placeholder hide={imgLoaded} aspectRatio={aspectRatio}>
        <LazyImg
          src={item.image}
          alt={item.description || item.category}
          initialVisible={imgLoaded}
          style={{ display: 'block', width: '100%' }}
          onLoad={() => {
            loadedImageCache.add(item._id);
            setImgLoaded(true);
          }}
        />
      </Placeholder>
      {item.category && <span className="gallery-admin-category-badge">{item.category}</span>}
      <div className="gallery-admin-overlay">
        <div className="gallery-admin-overlay-actions">
          <Tooltip title={t('admin.editProduct')}>
            <IconButton
              size="small"
              className="gallery-admin-icon-btn"
              onClick={() => onEdit(item)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('admin.deleteProduct')}>
            <IconButton
              size="small"
              className="gallery-admin-icon-btn gallery-admin-icon-btn--danger"
              onClick={() => onDelete(item)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('admin.dragToReorder')}>
            <IconButton
              size="small"
              className="gallery-admin-icon-btn gallery-admin-drag-handle"
              {...attributes}
              {...listeners}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>
    </div>
  );
});

// ─── Main component ───────────────────────────────────────────────────────────

export default function GalleryAdminTab() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const galleryImageList = useSelector((state) => state.galleryImageList);
  const { loading, gallery = [], error } = galleryImageList;

  const galleryImageCreate = useSelector((state) => state.galleryImageCreate);
  const { loading: loadingCreate, success: successCreate, error: errorCreate } = galleryImageCreate;

  const galleryImageUpdate = useSelector((state) => state.galleryImageUpdate);
  const { loading: loadingUpdate, success: successUpdate, error: errorUpdate } = galleryImageUpdate;

  const galleryImageDelete = useSelector((state) => state.galleryImageDelete);
  const { loading: loadingDelete, success: successDelete, error: errorDelete } = galleryImageDelete;

  const categoryListState = useSelector((state) => state.categoryList);
  const { categories = [], loading: loadingCategories } = categoryListState;

  const categoryCreate = useSelector((state) => state.categoryCreate);
  const {
    loading: loadingCatCreate,
    success: successCatCreate,
    error: errorCatCreate,
  } = categoryCreate;

  const categoryUpdateState = useSelector((state) => state.categoryUpdate);
  const { success: successCatUpdate, error: errorCatUpdate } = categoryUpdateState;

  const categoryDeleteState = useSelector((state) => state.categoryDelete);
  const { success: successCatDelete, error: errorCatDelete } = categoryDeleteState;

  const userSignin = useSelector((state) => state.userSignin);
  const { userInfo } = userSignin;

  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);

  // Upload dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadPreview, setUploadPreview] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadDescriptionEn, setUploadDescriptionEn] = useState('');
  const [uploadDescriptionPt, setUploadDescriptionPt] = useState('');
  const [uploadUseTranslation, setUploadUseTranslation] = useState(false);
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadingToS3, setUploadingToS3] = useState(false);
  const [uploadS3Error, setUploadS3Error] = useState('');
  const fileInputRef = useRef(null);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editDescription, setEditDescription] = useState('');
  const [editDescriptionEn, setEditDescriptionEn] = useState('');
  const [editDescriptionPt, setEditDescriptionPt] = useState('');
  const [editUseTranslation, setEditUseTranslation] = useState(false);
  const [editCategory, setEditCategory] = useState('');

  // Gallery filter
  const [filterCategory, setFilterCategory] = useState('*');
  const [filterDesc, setFilterDesc] = useState('');
  const [sectionOpen, setSectionOpen] = useState(true);
  useScrollLock(uploadOpen || editOpen);

  // Category management
  const [catItems, setCatItems] = useState([]);
  const [activeCatId, setActiveCatId] = useState(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryNameEn, setNewCategoryNameEn] = useState('');
  const [newCategoryNamePt, setNewCategoryNamePt] = useState('');
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCatName, setEditingCatName] = useState('');
  const [editingCatNameEn, setEditingCatNameEn] = useState('');
  const [editingCatNamePt, setEditingCatNamePt] = useState('');
  const [localErrorCatDelete, setLocalErrorCatDelete] = useState(null);
  const [errorCatDeleteKey, setErrorCatDeleteKey] = useState(0);
  const [localErrorCatUpdate, setLocalErrorCatUpdate] = useState(null);
  const [errorCatUpdateKey, setErrorCatUpdateKey] = useState(0);
  const syncedRef = useRef(false);

  const sensors = useSensors(useSensor(PointerSensor));
  const colCount = useColCount();

  // Filtered view (display-only; doesn't affect drag-reorder order)
  const displayItems = useMemo(() => {
    let arr =
      filterCategory === '*' ? items : items.filter((img) => img.category === filterCategory);
    if (filterDesc.trim()) {
      const q = filterDesc.trim().toLowerCase();
      arr = arr.filter((img) => (img.description ?? '').toLowerCase().includes(q));
    }
    return arr;
  }, [items, filterCategory, filterDesc]);

  useEffect(() => {
    setItems(gallery);
  }, [gallery]);

  useEffect(() => {
    setCatItems(categories);
  }, [categories]);

  // Set default category when categories load
  useEffect(() => {
    if (categories.length > 0 && !uploadCategory) {
      setUploadCategory(categories[0].name);
    }
  }, [categories, uploadCategory]);

  useEffect(() => {
    dispatch(listGalleryImages());
  }, [dispatch]);

  useEffect(() => {
    if (successCreate) {
      dispatch({ type: GALLERY_IMAGE_CREATE_RESET });
      dispatch(listGalleryImages());
      setUploadOpen(false);
      resetUploadForm();
    } else if (successUpdate) {
      dispatch({ type: GALLERY_IMAGE_UPDATE_RESET });
      dispatch(listGalleryImages());
      setEditOpen(false);
    } else if (successDelete) {
      dispatch({ type: GALLERY_IMAGE_DELETE_RESET });
      dispatch(listGalleryImages());
    }
  }, [dispatch, successCreate, successUpdate, successDelete]);

  // Auto-sync image-derived categories into DB (runs once after both loads are ready)
  useEffect(() => {
    if (syncedRef.current || loading || loadingCategories || gallery.length === 0) return;
    const dbNames = new Set(categories.map((c) => c.name));
    const missing = [...new Set(gallery.map((img) => img.category).filter(Boolean))].filter(
      (n) => !dbNames.has(n)
    );
    if (missing.length === 0) {
      syncedRef.current = true;
      return;
    }
    syncedRef.current = true;
    missing.forEach((name) => dispatch(createCategory(name)));
  }, [dispatch, loading, loadingCategories, gallery, categories]);

  useEffect(() => {
    if (errorCatDelete) {
      setLocalErrorCatDelete(errorCatDelete);
      setErrorCatDeleteKey((k) => k + 1);
    }
  }, [errorCatDelete]);

  useEffect(() => {
    if (errorCatUpdate) {
      setLocalErrorCatUpdate(errorCatUpdate);
      setErrorCatUpdateKey((k) => k + 1);
    }
  }, [errorCatUpdate]);

  // Fetch/refetch categories after mutations
  useEffect(() => {
    if (successCatCreate) {
      dispatch({ type: CATEGORY_CREATE_RESET });
      setNewCategoryName('');
    } else if (successCatUpdate) {
      dispatch({ type: CATEGORY_UPDATE_RESET });
      setEditingCatId(null);
      dispatch(listGalleryImages());
    } else if (successCatDelete) {
      dispatch({ type: CATEGORY_DELETE_RESET });
    }
    dispatch(listCategories());
  }, [dispatch, successCatCreate, successCatUpdate, successCatDelete]);

  // ── Upload ──

  const resetUploadForm = () => {
    setUploadPreview('');
    setUploadFile(null);
    setUploadDescription('');
    setUploadDescriptionEn('');
    setUploadDescriptionPt('');
    setUploadUseTranslation(false);
    setUploadCategory(categories[0]?.name || '');
    setUploadS3Error('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = async (e) => {
    const raw = e.target.files?.[0] || e.dataTransfer?.files?.[0];
    if (!raw) return;
    const file = await convertIfHeic(raw);
    setUploadPreview(URL.createObjectURL(file));
    setUploadFile(file);
    setUploadS3Error('');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFileChange({ target: { files: [e.dataTransfer.files[0]] } });
  };

  const handleUploadSubmit = async () => {
    if (!uploadFile || !uploadCategory) return;
    setUploadingToS3(true);
    setUploadS3Error('');
    try {
      const formData = new FormData();
      formData.append('image', uploadFile);
      const { data } = await Axios.post('/api/uploads/s3?folder=gallery', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${userInfo.token}`,
        },
      });
      dispatch(
        createGalleryImage({
          image: data.location,
          description: uploadDescription,
          descriptionEn: uploadDescriptionEn,
          descriptionPt: uploadDescriptionPt,
          useDescriptionTranslation: uploadUseTranslation,
          category: uploadCategory,
          ...(data.width && data.height ? { width: data.width, height: data.height } : {}),
        })
      );
    } catch (err) {
      setUploadS3Error(err.response?.data?.message || err.message);
    } finally {
      setUploadingToS3(false);
    }
  };

  // ── Edit ──

  const openEdit = useCallback((item) => {
    setEditItem(item);
    setEditDescription(item.description || '');
    setEditDescriptionEn(item.descriptionEn || '');
    setEditDescriptionPt(item.descriptionPt || '');
    setEditUseTranslation(item.useDescriptionTranslation || false);
    setEditCategory(item.category);
    setEditOpen(true);
  }, []);

  const handleEditSubmit = () => {
    if (!editItem) return;
    dispatch(
      updateGalleryImage(editItem._id, {
        description: editDescription,
        descriptionEn: editDescriptionEn,
        descriptionPt: editDescriptionPt,
        useDescriptionTranslation: editUseTranslation,
        category: editCategory,
      })
    );
  };

  // ── Delete ──

  const handleDelete = useCallback(
    (item) => {
      Swal.fire({
        title: t('admin.deleteImageTitle'),
        text: t('admin.deleteImageText'),
        showCancelButton: true,
        confirmButtonText: t('admin.deleteImageBtn'),
        confirmButtonColor: '#d33',
      }).then((result) => {
        if (result.isConfirmed) dispatch(deleteGalleryImage(item._id));
      });
    },
    [dispatch]
  );

  // ── Category delete ──

  const handleDeleteCategory = (cat) => {
    Swal.fire({
      title: t('admin.deleteCategoryTitle', { name: displayName(cat, i18n.language) }),
      showCancelButton: true,
      confirmButtonText: t('admin.deleteImageBtn'),
      confirmButtonColor: '#d33',
    }).then((result) => {
      if (result.isConfirmed) dispatch(deleteCategory(cat._id));
    });
  };

  // ── Drag and drop ──

  const handleCatDragEnd = useCallback(
    ({ active, over }) => {
      setActiveCatId(null);
      if (!over || active.id === over.id) return;
      setCatItems((prev) => {
        const oldIndex = prev.findIndex((c) => c._id === active.id);
        const newIndex = prev.findIndex((c) => c._id === over.id);
        const reordered = arrayMove(prev, oldIndex, newIndex);
        dispatch(reorderCategories(reordered.map((c, idx) => ({ _id: c._id, order: idx }))));
        return reordered;
      });
    },
    [dispatch]
  );

  const handleDragStart = ({ active }) => setActiveId(active.id);

  const handleDragEnd = useCallback(
    ({ active, over }) => {
      setActiveId(null);
      if (!over || active.id === over.id) return;
      setItems((prev) => {
        const oldIndex = prev.findIndex((i) => i._id === active.id);
        const newIndex = prev.findIndex((i) => i._id === over.id);
        const reordered = arrayMove(prev, oldIndex, newIndex);
        dispatch(
          reorderGalleryImages(reordered.map((item, idx) => ({ _id: item._id, order: idx })))
        );
        return reordered;
      });
    },
    [dispatch]
  );

  const activeItem = activeId ? items.find((i) => i._id === activeId) : null;

  // catItems is the ordered DB list; merge with any image-derived names not yet in DB
  const { dbCategoryByName, allCategoryNames } = useMemo(() => {
    const byName = Object.fromEntries(catItems.map((c) => [c.name, c]));
    const dbNames = new Set(catItems.map((c) => c.name));
    const extras = [...new Set(items.map((img) => img.category).filter(Boolean))]
      .filter((n) => !dbNames.has(n))
      .sort();
    return {
      dbCategoryByName: byName,
      allCategoryNames: [...catItems.map((c) => c.name), ...extras],
    };
  }, [catItems, items]);

  const sortableIds = useMemo(() => displayItems.map((i) => i._id), [displayItems]);

  const columns = useMemo(() => {
    const n = displayItems.length;
    const cols = Array.from({ length: colCount }, () => []);
    const fullRows = Math.floor(n / colCount);
    const extra = n % colCount;
    for (let row = 0; row < fullRows; row++) {
      for (let ci = 0; ci < colCount; ci++) {
        cols[ci].push(displayItems[row * colCount + ci]);
      }
    }
    const startCol = colCount - extra;
    for (let i = 0; i < extra; i++) {
      cols[startCol + i].push(displayItems[fullRows * colCount + i]);
    }
    return cols;
  }, [displayItems, colCount]);

  const handleAddCategory = () => {
    const name = newCategoryName.trim();
    if (!name) return;
    dispatch(createCategory(name, newCategoryNameEn, newCategoryNamePt));
    setNewCategoryNameEn('');
    setNewCategoryNamePt('');
  };

  const categorySelect = (value, setter) => (
    <FormControl fullWidth margin="normal" size="small">
      <InputLabel>{t('admin.category')}</InputLabel>
      <Select value={value} label={t('admin.category')} onChange={(e) => setter(e.target.value)}>
        {allCategoryNames.map((name) => (
          <MenuItem key={name} value={name}>
            {displayName(dbCategoryByName[name], i18n.language)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <div className="gallery-admin-table" style={{ marginBottom: '50px' }}>
      <LoadingOverlay loading={loadingDelete || loadingCreate || loadingUpdate || loadingCatCreate}>
        <Paper className="paper" style={{ backgroundColor: '#F4F4F4' }}>
          {errorDelete && <MessageBox variant="error">{errorDelete}</MessageBox>}
          {errorCreate && <MessageBox variant="error">{errorCreate}</MessageBox>}
          {errorUpdate && <MessageBox variant="error">{errorUpdate}</MessageBox>}
          {errorCatCreate && errorCatCreate !== 'Category already exists' && (
            <MessageBox variant="error">{errorCatCreate}</MessageBox>
          )}
          <Toolbar sx={{ flexDirection: 'column', alignItems: 'stretch', py: 1, gap: 1 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => setSectionOpen((v) => !v)}
            >
              <Typography className="title" variant="h6" component="div" sx={{ flexGrow: 1 }}>
                <b>{t('admin.gallery')}</b>
              </Typography>
              <IconButton tabIndex={-1}>
                {sectionOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Box>
            {sectionOpen && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder={t('admin.searchDescription')}
                  value={filterDesc}
                  onChange={(e) => setFilterDesc(e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <SearchIcon fontSize="small" sx={{ mr: 0.5, color: '#888' }} />
                      ),
                    },
                  }}
                  sx={{ flexGrow: 1 }}
                />
                <Tooltip title={t('admin.addImage')}>
                  <IconButton aria-label={t('admin.addImage')} onClick={() => setUploadOpen(true)}>
                    <AddIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Toolbar>

          <Collapse in={sectionOpen}>
            <div style={{ padding: '0 16px 12px', borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" style={{ marginBottom: 8, color: '#555' }}>
                <b>{t('admin.categories')}</b>
              </Typography>
              {localErrorCatDelete && (
                <div style={{ marginBottom: 8 }}>
                  <MessageBox key={errorCatDeleteKey} variant="error" autoDismiss={3000}>
                    {localErrorCatDelete}
                  </MessageBox>
                </div>
              )}
              {localErrorCatUpdate && (
                <div style={{ marginBottom: 8 }}>
                  <MessageBox key={errorCatUpdateKey} variant="error" autoDismiss={3000}>
                    {localErrorCatUpdate}
                  </MessageBox>
                </div>
              )}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={({ active }) => setActiveCatId(active.id)}
                onDragEnd={handleCatDragEnd}
                onDragCancel={() => setActiveCatId(null)}
              >
                <SortableContext items={catItems.map((c) => c._id)} strategy={rectSortingStrategy}>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 8,
                      alignItems: 'center',
                      marginBottom: 10,
                    }}
                  >
                    {allCategoryNames.map((name) => {
                      const dbCat = dbCategoryByName[name];
                      const isEditing = editingCatId === dbCat?._id;

                      if (isEditing) {
                        return (
                          <div
                            key={name}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              border: '1px solid #bbb',
                              borderRadius: 16,
                              padding: '2px 6px',
                              background: '#e0e0e0',
                            }}
                          >
                            <input
                              autoFocus
                              value={editingCatName}
                              size={Math.max(8, editingCatName.length + 2)}
                              onChange={(e) => setEditingCatName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && editingCatName.trim())
                                  dispatch(
                                    updateCategory(
                                      dbCat._id,
                                      editingCatName,
                                      editingCatName,
                                      editingCatNamePt
                                    )
                                  );
                                if (e.key === 'Escape') setEditingCatId(null);
                              }}
                              style={{
                                border: 'none',
                                outline: 'none',
                                background: 'transparent',
                                fontSize: '0.8rem',
                                fontFamily: 'inherit',
                              }}
                            />
                            <input
                              value={editingCatNamePt}
                              size={Math.max(8, editingCatNamePt.length + 2)}
                              onChange={(e) => setEditingCatNamePt(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && editingCatName.trim())
                                  dispatch(
                                    updateCategory(
                                      dbCat._id,
                                      editingCatName,
                                      editingCatName,
                                      editingCatNamePt
                                    )
                                  );
                                if (e.key === 'Escape') setEditingCatId(null);
                              }}
                              placeholder="PT"
                              style={{
                                border: 'none',
                                outline: 'none',
                                background: 'transparent',
                                fontSize: '0.8rem',
                                fontFamily: 'inherit',
                                opacity: 0.7,
                              }}
                            />
                            <IconButton
                              size="small"
                              sx={{ padding: '2px' }}
                              onClick={() => {
                                if (editingCatName.trim())
                                  dispatch(
                                    updateCategory(
                                      dbCat._id,
                                      editingCatName,
                                      editingCatName,
                                      editingCatNamePt
                                    )
                                  );
                              }}
                            >
                              <EditIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                            <Tooltip title={t('admin.cancel')}>
                              <IconButton
                                size="small"
                                sx={{ padding: '2px' }}
                                onClick={() => setEditingCatId(null)}
                              >
                                <CloseIcon sx={{ fontSize: 13 }} />
                              </IconButton>
                            </Tooltip>
                          </div>
                        );
                      }

                      return (
                        <SortableCategoryChip
                          key={name}
                          id={dbCat?._id || name}
                          name={displayName(dbCat, i18n.language)}
                          dbCat={dbCat}
                          isActive={activeCatId === dbCat?._id}
                          onEdit={() => {
                            setEditingCatId(dbCat._id);
                            setEditingCatName(name);
                            setEditingCatNamePt(dbCat.namePt || '');
                          }}
                          onDelete={() => handleDeleteCategory(dbCat)}
                        />
                      );
                    })}
                    {allCategoryNames.length === 0 && (
                      <span style={{ color: '#aaa', fontSize: '0.85rem' }}>
                        {t('admin.noCategoriesYet')}
                      </span>
                    )}
                  </div>
                </SortableContext>
                <DragOverlay>
                  {activeCatId ? (
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        border: '1px solid rgba(0,0,0,0.12)',
                        borderRadius: 16,
                        padding: '2px 10px',
                        background: '#e0e0e0',
                        fontSize: '0.8rem',
                        fontFamily: 'inherit',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        cursor: 'grabbing',
                      }}
                    >
                      {displayName(
                        catItems.find((c) => c._id === activeCatId),
                        i18n.language
                      )}
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder={t('admin.nameEn')}
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                  style={{ width: 160 }}
                />
                <TextField
                  size="small"
                  placeholder={t('admin.namePt')}
                  value={newCategoryNamePt}
                  onChange={(e) => setNewCategoryNamePt(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                  style={{ width: 160 }}
                />
                <IconButton
                  size="small"
                  onClick={handleAddCategory}
                  disabled={!newCategoryName.trim() || loadingCatCreate}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </div>
            </div>

            {/* ── Category filter ── */}
            {allCategoryNames.length > 0 && (
              <div
                style={{
                  padding: '4px 16px 12px',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                  alignItems: 'center',
                }}
              >
                <span
                  style={{
                    fontSize: '0.75rem',
                    color: '#888',
                    marginRight: 4,
                    fontFamily: 'inherit',
                  }}
                >
                  {t('admin.filter')}:
                </span>
                {[
                  { label: t('admin.all'), value: '*' },
                  ...allCategoryNames.map((n) => ({ label: n, value: n })),
                ].map(({ label, value }) => (
                  <div
                    key={value}
                    onClick={() => setFilterCategory(value)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      border: '1px solid rgba(0,0,0,0.12)',
                      borderRadius: 16,
                      padding: '2px 10px',
                      fontSize: '0.8rem',
                      fontFamily: 'inherit',
                      cursor: 'pointer',
                      userSelect: 'none',
                      background: filterCategory === value ? '#555' : '#e0e0e0',
                      color: filterCategory === value ? '#fff' : 'inherit',
                      transition: 'background 0.15s, color 0.15s',
                    }}
                  >
                    {label}
                  </div>
                ))}
              </div>
            )}

            <div style={{ padding: '0 16px 16px' }}>
              <div className="gallery-admin-scroll-container">
                {error ? (
                  <MessageBox variant="error">{error}</MessageBox>
                ) : (
                  <LoadingOverlay loading={loading} minHeight="30vh">
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                      onDragCancel={() => setActiveId(null)}
                    >
                      <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
                        <div className="gallery-admin-grid">
                          {columns.map((col, ci) => (
                            <div key={ci} className="gallery-admin-col">
                              {col.map((item) => (
                                <SortableCard
                                  key={item._id}
                                  item={item}
                                  onEdit={openEdit}
                                  onDelete={handleDelete}
                                  isActive={item._id === activeId}
                                />
                              ))}
                            </div>
                          ))}
                        </div>
                      </SortableContext>
                      <DragOverlay>
                        {activeItem ? <PlainCard item={activeItem} /> : null}
                      </DragOverlay>
                    </DndContext>
                  </LoadingOverlay>
                )}
                {!loading && !error && displayItems.length === 0 && (
                  <p style={{ textAlign: 'center', color: '#888', padding: '40px 0' }}>
                    {filterCategory === '*' && !filterDesc
                      ? t('admin.noImagesYet')
                      : t('admin.noImagesFilter')}
                  </p>
                )}
              </div>
            </div>
          </Collapse>
        </Paper>
      </LoadingOverlay>

      {/* ── Upload dialog ── */}
      <Dialog
        open={uploadOpen}
        onClose={() => {
          setUploadOpen(false);
          resetUploadForm();
        }}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        <DialogTitle>{t('admin.addGalleryImage')}</DialogTitle>
        <DialogContent>
          <LoadingOverlay loading={uploadingToS3}>
            <div
              className="gallery-admin-dropzone"
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploadPreview ? (
                <img src={uploadPreview} alt="preview" className="gallery-admin-preview" />
              ) : (
                <span>{t('admin.dropImageHere')}</span>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </div>
            {uploadS3Error && <MessageBox variant="error">{uploadS3Error}</MessageBox>}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginTop: 8,
                marginBottom: 4,
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={uploadUseTranslation}
                onChange={(e) => setUploadUseTranslation(e.target.checked)}
              />
              {t('admin.useDescriptionTranslation')}
            </label>
            {!uploadUseTranslation ? (
              <TextField
                label={t('admin.description')}
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                fullWidth
                margin="normal"
                size="small"
              />
            ) : (
              <div style={{ display: 'flex', gap: 8 }}>
                <TextField
                  label={t('admin.descriptionEn')}
                  value={uploadDescriptionEn}
                  onChange={(e) => setUploadDescriptionEn(e.target.value)}
                  fullWidth
                  margin="normal"
                  size="small"
                />
                <TextField
                  label={t('admin.descriptionPt')}
                  value={uploadDescriptionPt}
                  onChange={(e) => setUploadDescriptionPt(e.target.value)}
                  fullWidth
                  margin="normal"
                  size="small"
                />
              </div>
            )}
            {categorySelect(uploadCategory, setUploadCategory)}
          </LoadingOverlay>
        </DialogContent>
        <DialogActions>
          <button
            className="secondary"
            onClick={() => {
              setUploadOpen(false);
              resetUploadForm();
            }}
          >
            {t('admin.cancel')}
          </button>
          <button
            className="primary"
            onClick={handleUploadSubmit}
            disabled={!uploadFile || uploadingToS3 || loadingCreate || !uploadCategory}
          >
            {t('admin.addToGallery')}
          </button>
        </DialogActions>
      </Dialog>

      {/* ── Edit dialog ── */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        <DialogTitle>{t('admin.editImage')}</DialogTitle>
        <DialogContent>
          {editItem && (
            <img
              src={editItem.image}
              alt={editItem.description || editItem.category}
              className="gallery-admin-preview"
              style={{ marginBottom: 16 }}
            />
          )}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 8,
              marginBottom: 4,
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={editUseTranslation}
              onChange={(e) => setEditUseTranslation(e.target.checked)}
            />
            {t('admin.useDescriptionTranslation')}
          </label>
          {!editUseTranslation ? (
            <TextField
              label={t('admin.description')}
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              fullWidth
              margin="normal"
              size="small"
            />
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <TextField
                label={t('admin.descriptionEn')}
                value={editDescriptionEn}
                onChange={(e) => setEditDescriptionEn(e.target.value)}
                fullWidth
                margin="normal"
                size="small"
              />
              <TextField
                label={t('admin.descriptionPt')}
                value={editDescriptionPt}
                onChange={(e) => setEditDescriptionPt(e.target.value)}
                fullWidth
                margin="normal"
                size="small"
              />
            </div>
          )}
          {categorySelect(editCategory, setEditCategory)}
        </DialogContent>
        <DialogActions>
          <button className="secondary" onClick={() => setEditOpen(false)}>
            {t('admin.cancel')}
          </button>
          <button className="primary" onClick={handleEditSubmit} disabled={loadingUpdate}>
            {t('admin.save')}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
